import { datadogRum } from '@datadog/browser-rum';

/**
 * Initialize the Datadog RUM agent
 * @returns {void}
 */
export function initializeDatadog() {
  // Guard: Only initialize if required environment variables are present
  if (
    !import.meta.env.REACT_APP_DATADOG_APPLICATION_ID ||
    !import.meta.env.REACT_APP_DATADOG_CLIENT_TOKEN
  ) {
    console.warn(
      'Datadog RUM not initialized: Missing required environment variables',
    );
    return;
  }

  // Guard: Only initialize in production environments
  // Skips development, test, and other non-production environments
  if (import.meta.env.MODE !== 'production') {
    console.info(
      `Datadog RUM not initialized: Running in ${
        import.meta.env.MODE
      } environment`,
    );
    return;
  }

  try {
    datadogRum.init({
      applicationId: import.meta.env.REACT_APP_DATADOG_APPLICATION_ID,
      clientToken: import.meta.env.REACT_APP_DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: import.meta.env.REACT_APP_DATADOG_SERVICE,
      env:
        import.meta.env.REACT_APP_DEPLOYMENT_ENV === 'ephemeral'
          ? 'development'
          : import.meta.env.REACT_APP_DEPLOYMENT_ENV,
      version: import.meta.env.REACT_APP_VERSION || '1.0.0',

      // Resource and performance tracking
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      trackFrustrations: true,

      // Sampling configuration
      sessionSampleRate: 100, // 100% of sessions are tracked
      sessionReplaySampleRate: 100, // 100% of tracked sessions have replay

      // Distributed tracing configuration
      // This is CRITICAL for linking frontend sessions with backend APM traces
      allowedTracingUrls: [
        // Main API
        import.meta.env.REACT_APP_API_URL && {
          match: (url) => url.startsWith(import.meta.env.REACT_APP_API_URL),
          propagatorTypes: ['tracecontext', 'datadog'],
        },
        // Payments API (GraphQL)
        import.meta.env.REACT_APP_PAYMENTS_API_URL && {
          match: (url) =>
            url.startsWith(import.meta.env.REACT_APP_PAYMENTS_API_URL),
          propagatorTypes: ['tracecontext', 'datadog'],
        },
        // Two-Way API
        import.meta.env.REACT_APP_TWO_WAY_API_URL && {
          match: (url) =>
            url.startsWith(import.meta.env.REACT_APP_TWO_WAY_API_URL),
          propagatorTypes: ['tracecontext', 'datadog'],
        },
      ].filter(Boolean), // Remove undefined entries

      // Sample 100% of XHR/fetch requests for distributed tracing
      traceSampleRate: 100,

      // Propagate trace context in request headers
      // This adds trace IDs to outgoing requests so backend can correlate
      allowFallbackToLocalStorage: true,

      // Track XHR and Fetch requests for APM correlation
      trackSessionAcrossSubdomains: true,

      // Enhanced error tracking
      beforeSend: (event) => {
        // Scrub sensitive query params from URLs across all event types.
        // NOTE: event.http does not exist in the Datadog RUM event schema;
        // the correct paths are event.resource.url, event.view.url, and
        // event.error.resource.url.
        const sensitiveParamPattern = /([?&])(token|session_id|apiKey)=[^&]*/gi;

        if (event.resource?.url) {
          event.resource.url = event.resource.url.replace(
            sensitiveParamPattern,
            '$1$2=REDACTED',
          );
        }

        if (event.view?.url) {
          event.view.url = event.view.url.replace(
            sensitiveParamPattern,
            '$1$2=REDACTED',
          );
        }

        if (event.error?.resource?.url) {
          event.error.resource.url = event.error.resource.url.replace(
            sensitiveParamPattern,
            '$1$2=REDACTED',
          );
        }

        // Add custom context to error events
        if (event.type === 'error') {
          event.context = {
            ...event.context,
            deployment: import.meta.env.REACT_APP_DEPLOYMENT_ENV,
            appVersion: import.meta.env.REACT_APP_VERSION,
          };
        }

        return true; // Return false to discard the event
      },

      // Telemetry configuration - reduce noise
      telemetrySampleRate: 20,

      // Compression for better performance
      compressIntakeRequests: true,

      // Enable experimental features for better insights
      enableExperimentalFeatures: ['clickmap'],

      // Session replay privacy level.
      // 'mask-user-input' records all page layout, text, clicks, and navigation
      // but masks the values typed into form inputs (username, password, etc.).
      // This prevents browser password managers and security tools from treating
      // the recording agent as a credential-harvesting script, which in some
      // environments caused a brief popup/flash when the user clicked on login fields.
      // Use 'allow' only if you explicitly need raw input values in replays.
      defaultPrivacyLevel: 'allow',
    });

    // Explicitly start session replay recording
    // Required for v6.x to ensure consistent session replay capture
    datadogRum.startSessionReplayRecording();

    console.info('Datadog RUM initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Datadog RUM:', error);
  }
}

/**
 * Set a user's metadata in Datadog
 * @param {Object} user - The user object
 * @param {string} user.id - The user's ID
 * @param {string} user.firstName - The user's first name
 * @param {string} user.lastName - The user's last name
 * @param {string} user.emailAddress - The user's email address
 * @param {string} user.username - The user's username
 * @param {string} user.organizationId - The user's organization ID
 * @param {string} user.userRole.description - The user's role
 * @param {string} user.userStatus.name - The user's status
 * @param {string} user.userTitle.name - The user's title
 * @param {string} user.lastSystemAccess - The user's last system access
 * @param {string} user.numberFailedLoginAttempts - The user's number of failed login attempts
 * @returns {void}
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#user-session
 */
export function setDatadogUser(user) {
  if (!user || !user.id) return;

  const nameParts = [user.firstName, user.lastName].filter(Boolean);
  const fullName = nameParts.join(' ');

  datadogRum.setUser({
    id: user.id,
    ...(fullName ? { name: fullName } : {}),
    email: user.emailAddress,
    username: user.username,
    organizationId: user.organizationId,
    role: user.userRole?.description,
    status: user.userStatus?.name,
    title: user.userTitle?.name,
    lastSystemAccess: user.lastSystemAccess,
    numberFailedLoginAttempts: user.numberFailedLoginAttempts,
  });
}

/**
 * Stop a Datadog RUM session and clear user identity.
 * Call this on logout.
 */
export function endDatadogSession() {
  datadogRum.stopSessionReplayRecording();
  datadogRum.clearUser();
}

/**
 * Add a feature flag evaluation to Datadog
 * @param key
 * @param value
 */
export function addFeatureFlagEvaluation(key: string, value: string) {
  datadogRum.addFeatureFlagEvaluation(key, value);
}
